import { PhoneResponse } from '@module/contact/model/PhoneResponse';
import { SupplierResponse } from '@module/supplier/model/SupplierResponse';
import { SupplierSubscription } from '@module/supplier/model/SupplierSubscription';
import {
  Consignee,
  AohContactResponse,
  PortAddress,
  SupplierAdditionalInfo,
  PagingResponse,
  PagingRequest
} from '../../../types';
import { mainRestApi } from '../rest-api';
import { SupplierPortsResponse } from '@module/supplier/model/SupplierPortsResponse';
import { SupplierTableItem } from '@module/supplier/model/SupplierTableItem';
import { SupplierLegalEntityDetailsResponse } from '@state/types/SupplierLegalEntityDetailsResponse';
import { SupplierLegalEntityDetailsDTO } from '@state/types/SupplierLegalEntityDetailsDTO';

export interface SupplierProfileForm {
  id?: string;
  username?: string;
  name: string;
  surname: string;
  email: string;
}

interface AgentRegistrationSupplier extends SupplierResponse {
  contact: {
    phone1: PhoneResponse & {
      countryId: number;
      number: string;
      fullNumber: '+73216549870';
    };
    phone2?: PhoneResponse & {
      countryId: number;
      number: string;
      fullNumber: string;
    };
  };
  worldwide: boolean;
}

export const supplierApi = mainRestApi.injectEndpoints({
  endpoints: (build) => ({
    saveSupplierProfileSettings: build.mutation<
      SupplierProfileForm,
      SupplierProfileForm
    >({
      query: (data) => ({
        url: '/secured/supplier/settings/profile/save',
        method: 'POST',
        data
      }),
      invalidatesTags: ['AGENT_PROFILE']
    }),
    saveAutoQuoteSupplierSetting: build.mutation<SupplierProfileForm, boolean>({
      query: (hasAutoQuoteEnabled) => ({
        url: `/secured/supplier/auto-quote/save?hasAutoQuoteEnabled=${hasAutoQuoteEnabled}`,
        method: 'POST'
      }),
      invalidatesTags: ['SUPPLIER_DETAILS', 'AGENT_PROFILE']
    }),
    loadSupplierDetails: build.query<AgentRegistrationSupplier, void>({
      query: () => ({
        url: '/secured/supplier/load',
        method: 'GET'
      }),
      providesTags: ['SUPPLIER_DETAILS']
    }),
    loadSupplierSubscription: build.query<SupplierSubscription, void>({
      query: () => ({
        url: '/secured/supplier/settings/profile/subscription',
        method: 'GET'
      })
    }),
    loadSupplierAdditionalInfo: build.query<SupplierAdditionalInfo, void>({
      query: () => ({
        url: '/secured/supplier/settings/profile/additional-info',
        method: 'GET'
      }),
      providesTags: ['AGENT_ADDITIONAL_INFO']
    }),
    updateSupplierAdditionalInfo: build.mutation<
      SupplierAdditionalInfo,
      {
        prefundingRequired: boolean;
        canProvideCtmSameDay: boolean;
        prefundingAmount: number | null;
      }
    >({
      query: (data) => ({
        url: '/secured/supplier/settings/profile/additional-info',
        method: 'PUT',
        data
      }),
      invalidatesTags: ['AGENT_ADDITIONAL_INFO', 'AGENT_PROFILE']
    }),
    loadSupplierLegalEntityDetails: build.query<
      SupplierLegalEntityDetailsResponse,
      void
    >({
      query: () => ({
        url: '/secured/supplier/settings/legal-entity-details',
        method: 'GET'
      }),
      providesTags: ['SUPPLIER_LEGAL_ENTITY_DETAILS']
    }),
    saveSupplierLegalEntityDetails: build.mutation<
      SupplierLegalEntityDetailsResponse,
      SupplierLegalEntityDetailsDTO
    >({
      query: (data) => ({
        url: '/secured/supplier/settings/legal-entity-details',
        method: 'POST',
        data
      }),
      invalidatesTags: ['SUPPLIER_LEGAL_ENTITY_DETAILS']
    }),
    updateSupplierLogo: build.mutation<
      SupplierAdditionalInfo,
      {
        file: File;
      }
    >({
      query: (data) => ({
        url: '/secured/supplier/settings/profile/logo',
        method: 'PUT',
        data,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }),
      invalidatesTags: ['AGENT_PROFILE', 'AGENT_ADDITIONAL_INFO']
    }),
    getPortAddress: build.query<
      PortAddress,
      { supplierId: string; portAddressId: string }
    >({
      query: ({ supplierId, portAddressId }) => ({
        url: `/secured/supplier/${supplierId}/port-addresses/${portAddressId}`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result ? [{ type: 'AGENT_PORT_ADDRESS', id: result.id }] : []
    }),
    getPagedPortAddresses: build.query<
      {
        items: PortAddress[];
        totalItems: number;
        hasNextPage: boolean;
      },
      void
    >({
      query: () => ({
        url: `/secured/supplier/settings/get-port-addresses`,
        method: 'POST'
      }),
      providesTags: (result) =>
        result ? [{ type: 'AGENT_PORT_ADDRESS', id: 'LIST' }] : []
    }),
    savePortAddress: build.mutation<
      void,
      {
        supplierId: string;
        street: string;
        postcode: string;
        city: string;
        countryId: string;
        portIds: string[];
        ownedPresence: boolean;
      }
    >({
      query: ({ supplierId, ...data }) => ({
        url: `/secured/supplier/${supplierId}/port-addresses`,
        method: 'POST',
        data
      }),
      invalidatesTags: ['AGENT_PORT_ADDRESS', 'AGENT_PROFILE']
    }),

    updatePortAddress: build.mutation<
      void,
      {
        supplierId: string;
        portAddressId: number;
        street: string;
        postcode: string;
        city: string;
        countryId: number;
        portIds: number[];
        ownedPresence: boolean;
      }
    >({
      query: ({ supplierId, portAddressId, ...data }) => ({
        url: `/secured/supplier/${supplierId}/port-addresses/${portAddressId}`,
        method: 'PUT',
        data
      }),
      invalidatesTags: ['AGENT_PORT_ADDRESS', 'AGENT_PROFILE']
    }),
    deletePortAddress: build.mutation<
      void,
      { supplierId: string; portAddressId: number }
    >({
      query: ({ supplierId, portAddressId }) => ({
        url: `/secured/supplier/${supplierId}/port-addresses/${portAddressId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['AGENT_PORT_ADDRESS', 'AGENT_PROFILE']
    }),
    getConsignee: build.query<Consignee, { consigneeId: string }>({
      query: ({ consigneeId }) => ({
        url: `/secured/supplier/settings/consignees/${consigneeId}`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result ? [{ type: 'AGENT_CONSIGNEE', id: result.id }] : []
    }),
    getPageedConsignees: build.query<
      {
        items: Consignee[];
        totalItems: number;
        hasNextPage: boolean;
      },
      void
    >({
      query: () => ({
        url: `/secured/supplier/settings/get-consignees`,
        method: 'POST'
      }),
      providesTags: (result) =>
        result ? [{ type: 'AGENT_CONSIGNEE', id: 'LIST' }] : []
    }),
    saveConsignee: build.mutation<
      void,
      {
        name: string;
        street: string;
        postcode: string;
        city: string;
        countryId: string;
        phoneNumber: string;
        phoneCountryId: string;
        faxNumber: string;
        faxCountryId: string;
        portIds: string[];
      }
    >({
      query: (data) => ({
        url: '/secured/supplier/settings/consignees',
        method: 'POST',
        data
      }),
      invalidatesTags: ['AGENT_CONSIGNEE', 'AGENT_PROFILE']
    }),
    updateConsignee: build.mutation<
      void,
      {
        consigneeId: number;
        name: string;
        street: string;
        postcode: string;
        city: string;
        countryId: string;
        phoneNumber: string;
        phoneCountryId: string;
        faxNumber: string;
        faxCountryId: string;
        portIds: string[];
      }
    >({
      query: ({ consigneeId, ...data }) => ({
        url: `/secured/supplier/settings/consignees/${consigneeId}`,
        method: 'PUT',
        data
      }),
      invalidatesTags: ['AGENT_CONSIGNEE', 'AGENT_PROFILE']
    }),
    deleteConsignee: build.mutation<void, { consigneeId: string }>({
      query: ({ consigneeId }) => ({
        url: `/secured/supplier/settings/consignees/${consigneeId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['AGENT_CONSIGNEE', 'AGENT_PROFILE']
    }),
    loadAgentPorts: build.query<SupplierPortsResponse, { countryId?: string }>({
      query: ({ countryId }) => ({
        url: '/secured/supplier/settings/ports/load',
        method: 'GET',
        params: {
          countryId: countryId?.trim()
        }
      }),
      providesTags: ['AGENT_PORTS']
    }),
    loadAgentPortsBySupplierId: build.query<
      SupplierPortsResponse,
      { supplierId: string; countryId?: string }
    >({
      query: ({ supplierId, countryId }) => ({
        url: `/secured/supplier/${supplierId}/ports`,
        method: 'GET',
        params: {
          countryId: countryId?.trim()
        }
      }),
      providesTags: ['AGENT_PORTS']
    }),
    getAohContact: build.query<AohContactResponse, { contactId: string }>({
      query: ({ contactId }) => ({
        url: `/secured/supplier/settings/aoh-contacts/${contactId}`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result ? [{ type: 'AGENT_AOH_CONTACT', id: result.id }] : []
    }),
    getPagedAohContacts: build.query<
      {
        items: AohContactResponse[];
        totalItems: number;
        hasNextPage: boolean;
      },
      void
    >({
      query: () => ({
        url: `/secured/supplier/settings/get-aoh-contacts`,
        method: 'POST'
      }),
      providesTags: (result) =>
        result ? [{ type: 'AGENT_AOH_CONTACT', id: 'LIST' }] : []
    }),
    saveAohContact: build.mutation<
      void,
      {
        phoneNumber: string;
        phoneCountryId: string;
        portIds: string[];
      }
    >({
      query: (data) => ({
        url: '/secured/supplier/settings/aoh-contacts',
        method: 'POST',
        data
      }),
      invalidatesTags: ['AGENT_AOH_CONTACT', 'AGENT_PROFILE']
    }),
    updateAohContact: build.mutation<
      void,
      {
        contactId: number;
        phoneNumber: string;
        phoneCountryId: string;
        portIds: string[];
      }
    >({
      query: ({ contactId, ...data }) => ({
        url: `/secured/supplier/settings/aoh-contacts/${contactId}`,
        method: 'PUT',
        data
      }),
      invalidatesTags: ['AGENT_AOH_CONTACT', 'AGENT_PROFILE']
    }),
    deleteAohContact: build.mutation<void, { contactId: string }>({
      query: ({ contactId }) => ({
        url: `/secured/supplier/settings/aoh-contacts/${contactId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['AGENT_AOH_CONTACT', 'AGENT_PROFILE']
    }),
    searchSuppliers: build.query<
      PagingResponse<SupplierTableItem>,
      { useCase: string; data: PagingRequest }
    >({
      query: ({ useCase, data }) => ({
        url: '/secured/supplier/search',
        method: 'POST',
        params: {
          useCase
        },
        data
      })
    })
  }),
  overrideExisting: true
});

export const {
  useSaveSupplierProfileSettingsMutation,
  useSaveAutoQuoteSupplierSettingMutation,
  useLoadSupplierDetailsQuery,
  useLoadSupplierSubscriptionQuery,
  useLoadSupplierAdditionalInfoQuery,
  useLazyLoadSupplierAdditionalInfoQuery,
  useUpdateSupplierAdditionalInfoMutation,
  useUpdateSupplierLogoMutation,
  useLoadSupplierLegalEntityDetailsQuery,
  useSaveSupplierLegalEntityDetailsMutation,
  useSavePortAddressMutation,
  useUpdatePortAddressMutation,
  useGetPortAddressQuery,
  useDeletePortAddressMutation,
  useGetPagedPortAddressesQuery,
  useLazyGetPagedPortAddressesQuery,
  useGetConsigneeQuery,
  useGetPageedConsigneesQuery,
  useSaveConsigneeMutation,
  useUpdateConsigneeMutation,
  useDeleteConsigneeMutation,
  useLoadAgentPortsQuery,
  useLoadAgentPortsBySupplierIdQuery,
  useLazyLoadAgentPortsQuery,
  useGetAohContactQuery,
  useGetPagedAohContactsQuery,
  useSaveAohContactMutation,
  useUpdateAohContactMutation,
  useDeleteAohContactMutation,
  useSearchSuppliersQuery
} = supplierApi;

export const loadSupplierRequestCompletedMatcher =
  supplierApi.endpoints['loadSupplierDetails'].matchFulfilled;

export const invalidatePortAddress = () =>
  supplierApi.util.invalidateTags(['AGENT_PORT_ADDRESS']);
export const invalidateAgentConsignee = () =>
  supplierApi.util.invalidateTags(['AGENT_CONSIGNEE']);
export const invalidateAgentAohContact = () =>
  supplierApi.util.invalidateTags(['AGENT_AOH_CONTACT']);
